import React from 'react';
import { useForm } from 'react-hook-form';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Stripe,
  StripeCardElement,
  StripeCardElementChangeEvent,
} from '@stripe/stripe-js';

import Button from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { generateFaqNotes } from 'components/Payment/helpers';
import { StripeElement } from 'components/StripeElement';

import { useBusiness } from 'hooks/business/useBusiness';
import { useCancellation } from 'hooks/cancellation/useCancellation';

import Faq from 'routes/book/Faq';

import { formatCurrency } from 'utils/helpers';

import s from './Payment.module.css';

export type DepositInfo = {
  servicesNames?: string[];
  totalDeposit: number;
  totalFee: number;
};

type PaymentProps = {
  isLoading: boolean;
  clientName: string;
  onSubmit: ({
    cardElement,
    stripe,
  }: {
    cardElement: StripeCardElement;
    stripe: Stripe;
  }) => Promise<void>;
  paymentErrorMessage?: string;
  servicesDeposits: DepositInfo | DepositInfo[];
};

export const Payment = (props: PaymentProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [stripeError, setStripeError] = React.useState<string | undefined>(
    undefined,
  );
  const { cancellationPolicyFee } = useCancellation();
  const { name: businessName } = useBusiness();
  const [cardInputCompleted, setCardInputCompleted] = React.useState(false);
  const {
    isLoading,
    servicesDeposits,
    clientName,
    onSubmit,
    paymentErrorMessage,
  } = props;

  const handleStripeOnChange = (event: StripeCardElementChangeEvent) => {
    setStripeError(event.error?.message);
    setCardInputCompleted(event.complete);
  };

  const submitForm = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return false;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    if (!cardInputCompleted && cardElement) {
      const { error } = await stripe.createToken(cardElement, {
        name: clientName,
      });

      setStripeError(error?.message);

      return false;
    }

    if (cardElement) {
      void onSubmit({ cardElement, stripe });
    }
  };

  const totalDepositAmount = Array.isArray(servicesDeposits)
    ? servicesDeposits.reduce(
        (total, current) => total + current.totalDeposit,
        0,
      )
    : servicesDeposits?.totalDeposit;

  const totalDepositFee = Array.isArray(servicesDeposits)
    ? servicesDeposits.reduce((total, current) => total + current.totalFee, 0)
    : servicesDeposits?.totalFee;

  const showTransactionList =
    Array.isArray(servicesDeposits) && servicesDeposits.length > 1;

  const faqNotes = generateFaqNotes(
    businessName,
    cancellationPolicyFee,
    totalDepositAmount,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      transaction: false,
    },
  });

  return (
    <>
      {paymentErrorMessage && (
        <p className={s.paymentErrorMessage}>
          {`Whoops! ${paymentErrorMessage}`}
        </p>
      )}

      <form
        className={s.form}
        onSubmit={(event) => void handleSubmit(submitForm)(event)}
      >
        <div className={s.cardForm}>
          <StripeElement
            id="credit-card"
            onChange={handleStripeOnChange}
            errorMessage={stripeError}
          />
        </div>

        {totalDepositAmount > 0 && (
          <section className={s.additionalInfo}>
            <div className={s.deposits}>
              <h2 className={s.depositInfo}>
                {`${formatCurrency(
                  totalDepositAmount,
                  'USD',
                )} Non-Refundable Deposit Required`}
              </h2>
              <p>
                The deposit will be charged upon booking and applied to the
                appointment total. If you cancel or no show, you will lose your
                deposit.
              </p>
              {totalDepositFee > 0 && (
                <p>
                  An additional {formatCurrency(totalDepositFee, 'USD')} service
                  fee will apply to this charge.
                </p>
              )}

              {showTransactionList && (
                <Checkbox
                  className={s.checkbox}
                  labelClassName={s.checkboxLabel}
                  id="transaction"
                  name="transaction"
                  register={register('transaction', {
                    required: 'Required',
                  })}
                  error={errors?.transaction?.message}
                  label={
                    'Please acknowledge that you may see multiple transactions on your card statement.'
                  }
                />
              )}
            </div>
          </section>
        )}

        <Button className={s.button} loading={isLoading} disabled={isLoading}>
          Next
        </Button>

        <Faq notes={faqNotes} />
      </form>
    </>
  );
};
